// Shared translation of a caught video submit/resume error into user-facing copy.
// Used by PromptPage (submit) and ContentPage (resume) so their billing-error
// wording and "go to wallet" affordance can't drift apart.
//
// The engine returns structured billing failures on POST /api/v1/videos and
// /resume (see valoi_engine credits_api_contract.md §2/§3). apiClient surfaces
// them as ApiError with the parsed `detail`; we branch on status + code here.
import { ApiError } from '@/shared/api/apiClient';
import { safeErrorMessage } from '@/shared/utils/errors';
import { formatCredits } from '@/shared/utils/format';

export type JobErrorInfo = {
  message: string;
  // When true, the page should also offer a link to /wallet (add a card / funds).
  walletCta: boolean;
};


// Pull the error code out of a string OR object `detail`.
function codeOf(detail: unknown): string | undefined {
  if (typeof detail === 'string') return detail.trim();
  if (detail && typeof detail === 'object' && typeof (detail as { error?: unknown }).error === 'string') {
    return (detail as { error: string }).error;
  }
  return undefined;
}

export function describeJobError(err: unknown): JobErrorInfo {
  if (err instanceof ApiError) {
    const code = codeOf(err.detail);

    // No billing customer yet — route to add a card, not to a top-up amount.
    if (err.status === 402 && code === 'payment_method_required') {
      return { message: 'Add a payment method to generate videos.', walletCta: true };
    }

    // Has a card but not enough credits — route to top-up, with the exact shortfall.
    // `required`/`available` arrive as credits from the engine (1 credit = $1).
    if (err.status === 400 && code === 'insufficient_credits') {
      const detail = err.detail as { required?: number; available?: number };
      if (typeof detail?.required === 'number' && typeof detail?.available === 'number') {
        return {
          message: `You need ${formatCredits(detail.required)} credits but only have ${formatCredits(detail.available)} available.`,
          walletCta: true,
        };
      }
      return { message: "You don't have enough credits for this video.", walletCta: true };
    }

    // Rare safety cap — retrying the same input won't help; no wallet CTA.
    if (err.status === 400 && code === 'hold_above_maximum') {
      return { message: "This request can't be processed. Try a shorter submission.", walletCta: false };
    }

    // Everything else (transient billing errors, 409 resume conflicts, validation):
    // ApiError.message is already humanized by apiClient.
    return { message: err.message, walletCta: false };
  }

  // Not an ApiError: only authored copy (UserFacingError) may reach the banner —
  // a plain Error's message is internal text and falls back to generic copy.
  return { message: safeErrorMessage(err), walletCta: false };
}
